import { format, isPast, parseISO } from 'date-fns';
import { BigNumber, ethers } from 'ethers';
import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useContext, useEffect, useMemo, useState, VFC } from 'react';
import Countdown, { zeroPad } from 'react-countdown';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import v2FetchCreationByID from '@/apis/aniftyBackend/creations/v2FetchCreationByID';
import {
  MintAskStatus,
  useMintAsk,
} from '@/apis/aniftyBackend/mintAsks/getMintAsk';
import { useMintBidsByAskHash } from '@/apis/aniftyBackend/mintAsks/getMintBidsByAskHash';
import { amountFilled } from '@/apis/blockchain/AniftyMarketplaceV2Ipfs';
import {
  getAllowance,
  setAllowanceApproval,
} from '@/apis/blockchain/WrappedETH';
import DefaultSpinner from '@/components/DefaultSpinner';
import FramedIcon from '@/components/FramedIcon';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import ClaimAuctionModal from '@/containers/AuctionActivity/AuctionActivityCard/ClaimAuctionModal';
import { ProfileContext } from '@/contexts/ProfileProvider';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useETHPrice from '@/hooks/useETHPrice';
import useWalletAddress from '@/hooks/useWalletAddress';
import GreaterThanIcon from '@/icons/greater-than-icon.svg';
import LessThanIcon from '@/icons/less-than-icon.svg';
import { captureException } from '@/utils/Sentry';

import AuctionHistory from './AuctionHistory';
import ConfirmBidModal from './ConfirmBidModal';
import useTryToAuthorize from '@/hooks/useTryToAuthorize';
import redirectTargets from '@/config/redirectTargets';
import styles from './CreationAuction.module.scss';

type Props = {
  creation: V2CreationData;
};

const CreationAuction: VFC<Props> = ({ creation }) => {
  const { t, i18n } = useTranslation();
  const [creationDataClient, setCreationDataClient] =
    useState<V2CreationData | null>({
      ...creation,
      mintAsk: null,
      aniftyTokenID: null,
    });

  const [isApproved, setIsApproved] = useState<undefined | boolean>(undefined);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [soldout, setSoldout] = useState<boolean>(false);
  const profileContext = useContext(ProfileContext);
  const web3Context = useContext(Web3Context);
  const { currentAccountAddress } = useWalletAddress();
  const { convertETHToLocalCurrency } = useETHPrice();
  const tryToAuthorize = useTryToAuthorize();

  const { data: mintAsk, isLoading: isLoadingMintAsk } = useMintAsk({
    askHash: creation.mintAsk.askHash,
  });

  const { data: allMintBids, isLoading: loadingAllMintBids } =
    useMintBidsByAskHash({
      askHash: creation.mintAsk.askHash,
      config: {
        refetchOnMount: 'always',
      },
    });

  const fetchCreationData = async () => {
    const fetchedCreationData = await v2FetchCreationByID(
      creationDataClient.creationID
    );

    setCreationDataClient({
      ...creationDataClient,
      mintAsk: fetchedCreationData.mintAsk,
      aniftyTokenID: fetchedCreationData.aniftyTokenID,
    });
  };

  const checkSoldOut = async () => {
    if (!web3Context.web3.AniftyMarketplaceV2Ipfs) {
      return;
    }

    try {
      const amountSold = await amountFilled(
        web3Context.web3.AniftyMarketplaceV2Ipfs,
        creation.mintAsk.askHash
      );
      setSoldout(amountSold.toString() === '1');
    } catch (e) {
      console.error(e);
      captureException(e);
    }
  };

  const isMyCreation: boolean =
    !!profileContext.userID &&
    profileContext.userID.toLowerCase() === creation.creator.userID;

  const highestBid = useMemo(() => {
    if (loadingAllMintBids) {
      return undefined;
    }

    return (
      allMintBids.sort(
        (a, b) =>
          Number(b.price) - Number(a.price) ||
          parseISO(b.bidAt).getTime() - parseISO(a.bidAt).getTime()
      )[0] || null
    );
  }, [allMintBids]);

  const auctionDeadLine = useMemo(
    () => (mintAsk?.endTime ? parseISO(mintAsk?.endTime) : null),
    [mintAsk]
  );

  const isDeadlineExceeded = auctionDeadLine && isPast(auctionDeadLine);

  const isFinished = mintAsk?.status === MintAskStatus.Finished;
  const isCancelled = mintAsk?.status === MintAskStatus.Cancelled;

  const myLatestBid = useMemo(() => {
    if (loadingAllMintBids) {
      return undefined;
    }

    if (!currentAccountAddress) {
      return null;
    }

    return (
      allMintBids
        .filter(
          (mintBid) => mintBid.signer.toLowerCase() === currentAccountAddress
        )
        .sort(
          (a, b) => parseISO(b.bidAt).getTime() - parseISO(a.bidAt).getTime()
        )[0] || null
    );
  }, [allMintBids, currentAccountAddress]);

  const othersLatestBid = useMemo(() => {
    if (loadingAllMintBids) {
      return undefined;
    }

    return (
      allMintBids
        .filter(
          (mintBid) => mintBid.signer.toLowerCase() !== currentAccountAddress
        )
        .sort(
          (a, b) => parseISO(b.bidAt).getTime() - parseISO(a.bidAt).getTime()
        )[0] || null
    );
  }, [allMintBids, currentAccountAddress]);

  const isWinning = useMemo(() => {
    return myLatestBid && myLatestBid.bidID === highestBid.bidID;
  }, [myLatestBid, highestBid]);

  const canBid = useMemo(() => {
    return !isCancelled && !isFinished && !isDeadlineExceeded;
  }, [isCancelled, isFinished, isDeadlineExceeded]);

  useEffect(() => {
    fetchCreationData();
  }, []);

  const minimumNextBidPrice = useMemo(() => {
    if (!mintAsk) return 0;

    return highestBid
      ? // Must be 10% greater than highest bid
        Math.ceil(
          Number(
            formatEther(BigNumber.from(highestBid.price).mul(11).div(10))
          ) * 100
        ) / 100
      : // No bid placed, just set to starting auction price
        Number(formatEther(mintAsk.askOrder.price));
  }, [highestBid, mintAsk]);

  const formattedWonAt = useMemo(() => {
    if (!highestBid) return null;

    return format(parseISO(highestBid.bidAt), "MMM dd, yyyy 'at' hh:mm aa");
  }, [highestBid]);

  const handleApproveButtonClick = async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    if (!web3Context.metamaskConnected) {
      setIsProcessing(false);
      await tryToAuthorize({
        redirectTarget: redirectTargets.creationDetail(creationDataClient.creationID)
      });
      return;
    }

    try {
      await setAllowanceApproval(
        web3Context.web3.WrappedETHContract,
        web3Context.web3.AniftyMarketplaceV2Ipfs.address,
        ethers.BigNumber.from(
          '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
        )
      );
      await checkApproved();
    } catch (e) {
      console.error(e);
      await tryToAuthorize({
        redirectTarget: redirectTargets.creationDetail(creationDataClient.creationID)
      });
      captureException(e);
    } finally {
      setIsProcessing(false);
    }
  };

  const checkApproved = async (): Promise<boolean> => {
    if (!web3Context.web3.AniftyERC1155Contract) {
      return;
    }

    try {
      const marketplaceAllowance = await getAllowance(
        web3Context.web3.WrappedETHContract,
        web3Context.currentAccountAddress,
        web3Context.web3.AniftyMarketplaceV2Ipfs.address
      );
      setIsApproved(marketplaceAllowance.gt(0));
    } catch (e) {
      console.error(e);
      captureException(e);
    }
  };

  const bidButtonText = useMemo(() => {
    if (isCancelled) return t('creationPage.claimButtonCancelled');

    if (isDeadlineExceeded) return t('creationPage.auctionDeadlineExceeded');

    return myLatestBid
      ? t('creationPage.bidAgainButton')
      : t('creationPage.bidButton');
  }, [isCancelled, isDeadlineExceeded, myLatestBid]);

  const bidNoticeText = useMemo(() => {
    if (myLatestBid) {
      if (isWinning) return t('creationPage.auctionWinning');

      return t('creationPage.auctionLosing');
    }

    if (canBid) return t('creationPage.noMyBidAdvise');

    return null;
  }, [myLatestBid, isWinning, canBid]);

  useEffect(() => {
    // Have to approve erc1155v2 to transfer erc1155v1
    // Load approval status on web3 context change
    checkApproved();
    checkSoldOut();
  }, [web3Context]);


  // For formatting countdown
  const renderer = ({ hours, minutes, seconds }) => (
    <span>
      {zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
    </span>
  )

  return (
    <>
      <ConfirmBidModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        creation={creation}
        minimumBidPrice={minimumNextBidPrice}
      />

      {loadingAllMintBids || isLoadingMintAsk ? (
        <DefaultSpinner />
      ) : (
        <>
          <div className={styles.creationAuctionCard}>
            {isFinished ? (
              <div className={styles.finishedAuction}>
                <div className={styles.bidInfo}>
                  <div className={styles.highestBidContainer}>
                    <p className={styles.highestBidLabel}>
                      {t('creationPage.soldFor')}
                    </p>
                    <p className={styles.bidStatusText}>
                      {allMintBids.length > 0 && highestBid
                        ? `${formatEther(highestBid.price)} WETH`
                        : t('creationPage.noBid')}
                    </p>
                    <p className={styles.highestBidDoller}>
                      {allMintBids.length > 0 && highestBid
                        ? convertETHToLocalCurrency(highestBid.price)
                        : `$0.00`}
                    </p>
                  </div>

                  <div className={styles.currentOwnerContainer}>
                    <p className={styles.currentOwnerLabel}>
                      {t('creationPage.currentOwner')}
                    </p>

                    <Link href={routes.myPage(highestBid.user.userID)}>
                      <a>
                        <FramedIcon
                          imageURL={
                            highestBid.user.profileImageURL ??
                            DEFAULT_PROFILE_IMAGE_PATH
                          }
                        />
                        <p className={styles.currentOwnerName}>
                          {highestBid.user.userName
                            ? `@${highestBid.user.userName}`
                            : highestBid.user.userID}
                        </p>
                      </a>
                    </Link>
                    <p className={styles.currentOwnerWonAt}>{formattedWonAt}</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.remainingTime}>
                  <p className={styles.remainingTimeLabel}>
                    {t('creationPage.remainingTime')}
                  </p>
                  <p className={styles.remainingTimeBody}>
                    {auctionDeadLine ? (
                      <Countdown date={auctionDeadLine} renderer={renderer} />
                    ) : (
                      '24:00:00'
                    )}
                  </p>
                </div>
                {isMyCreation ? (
                  <>
                    <div className={styles.bidInfo}>
                      <div className={styles.reservePriceContainer}>
                        <p className={styles.reservePriceLabel}>
                          {t('creationPage.reservePrice')}
                        </p>

                        <p className={styles.reservePriceBody}>
                          {formatEther(mintAsk.askOrder.price)}ETH
                        </p>
                        <p className={styles.reservePriceDoller}>
                          {convertETHToLocalCurrency(mintAsk.askOrder.price)}
                        </p>
                      </div>

                      <div className={styles.highestBidContainer}>
                        <p className={styles.highestBidLabel}>
                          {t('creationPage.highestBid')}
                        </p>
                        <p className={styles.bidStatusText}>
                          {allMintBids.length > 0
                            ? `${formatEther(highestBid.price)}ETH`
                            : t('creationPage.noBid')}
                        </p>
                        <p className={styles.highestBidDoller}>
                          {allMintBids.length > 0
                            ? convertETHToLocalCurrency(highestBid.price)
                            : `$0.00`}
                        </p>
                        {allMintBids.length <= 0 && (
                          <p className={styles.highestBidAdvise}>
                            {t('creationPage.highestBidAdvise')}
                          </p>
                        )}
                      </div>
                    </div>

                    <button
                      className={styles.claimButton}
                      type="button"
                      disabled={
                        allMintBids.length <= 0 ||
                        isCancelled ||
                        isProcessing ||
                        isFinished ||
                        soldout
                      }
                      onClick={() => setIsClaimModalOpen(true)}
                    >
                      {isCancelled
                        ? t('creationPage.claimButtonCancelled')
                        : t('creationPage.claimButton')}
                    </button>

                    {highestBid ? (
                      <ClaimAuctionModal
                        creation={creation}
                        isDeadlineExceeded={isDeadlineExceeded}
                        mintAsk={mintAsk}
                        bid={highestBid}
                        bidderName={
                          (i18n.language === 'ja' &&
                            highestBid.user.displayNameJA) ||
                          highestBid.user.displayName
                        }
                        isOpen={isClaimModalOpen}
                        onRequestClose={() => setIsClaimModalOpen(false)}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className={styles.bidInfo}>
                      <div className={styles.highestBidContainer}>
                        <p className={styles.highestBidLabel}>
                          {t('creationPage.highestBidByOthers')}
                        </p>
                        <p className={styles.bidStatusText}>
                          {allMintBids.length > 0 && othersLatestBid
                            ? `${formatEther(othersLatestBid.price)} ETH`
                            : t('creationPage.noBid')}
                        </p>
                        <p className={styles.highestBidDoller}>
                          {allMintBids.length > 0 && othersLatestBid
                            ? convertETHToLocalCurrency(othersLatestBid.price)
                            : `$0.00`}
                        </p>
                      </div>
                      {allMintBids.length > 0 && isWinning && (
                        <LessThanIcon className={styles.lessThanIcon} />
                      )}

                      {allMintBids.length > 0 && !isWinning && (
                        <GreaterThanIcon className={styles.greaterThanIcon} />
                      )}

                      <div className={styles.myBidContainer}>
                        <p className={styles.myBidLabel}>
                          {t('creationPage.yourBid')}
                        </p>

                        <p className={styles.bidStatusText}>
                          {myLatestBid
                            ? `${formatEther(myLatestBid.price)} WETH`
                            : t('creationPage.noMyBid')}
                        </p>
                        <p className={styles.myBidDoller}>
                          {myLatestBid
                            ? convertETHToLocalCurrency(myLatestBid.price)
                            : '$0.00'}
                        </p>
                        <p className={styles.highestBidAdvise}>
                          {bidNoticeText}
                        </p>
                      </div>
                    </div>
                    {isApproved ? (
                      <button
                        className={
                          myLatestBid ? styles.bidAgainButton : styles.bidButton
                        }
                        type="button"
                        onClick={() => setIsConfirmModalOpen(true)}
                        disabled={!canBid}
                      >
                        {bidButtonText}
                      </button>
                    ) : (
                      <button
                        className={styles.bidButton}
                        type="button"
                        onClick={handleApproveButtonClick}
                        disabled={isProcessing}
                      >
                        {t('creationPage.approveWETH')}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <AuctionHistory
            mintBids={allMintBids}
            creation={creation}
            listedPrice={mintAsk.askOrder.price}
            isFinished={isFinished}
            highestBid={highestBid}
          />
        </>
      )}
    </>
  );
};

export default CreationAuction;
